import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Button from './ui/Button';
import { useFavoris } from '../context/FavorisContext';
import { getImageUrl } from '../utils/imageUtils';

const ProduitCard = ({ produit, showArtisan = true, onRemoveFromFavoris, showViews = true }) => {
  const { isFavorite, toggleFavorite } = useFavoris();
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');

  const handleAddFavorite = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const result = await toggleFavorite(produit._id);
    
    // Si on retire des favoris et qu'on a une callback, l'appeler
    if (result.action === 'removed' && onRemoveFromFavoris) {
      onRemoveFromFavoris();
    }
    
    // Afficher une notification subtile
    setNotificationMessage(result.message);
    setShowNotification(true);
    
    // Masquer la notification après 2 secondes
    setTimeout(() => {
      setShowNotification(false);
    }, 2000);
  };

  const handleShare = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const shareData = {
      title: produit.nom,
      text: produit.description || produit.nom,
      url: `${window.location.origin}/produits/${produit._id}`,
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(shareData.url);
        alert('Lien copié dans le presse-papiers');
      } else {
        alert(shareData.url);
      }
    } catch (err) {
      console.error('Share failed', err);
    }
  };

  const generateWhatsAppMessage = () => {
    const produitUrl = `${window.location.origin}/produits/${produit._id}`;
    const message = `Bonjour, je veux commander ce produit: ${produit.nom} - ${produit.prix}F CFA\n\nLien du produit: ${produitUrl}`;
    return `https://wa.me/${produit.artisan?.whatsapp?.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(message)}`;
  };

  return (
    <div 
      className="bg-white dark:bg-gray-800 rounded-xl shadow-md md:hover:shadow-xl transition-all duration-300 md:hover:-translate-y-1 border border-gray-200 dark:border-gray-700 md:hover:border-primary-300 md:dark:hover:border-primary-600 overflow-hidden group relative w-full h-full flex flex-col"
      style={{ 
        touchAction: 'manipulation',
        WebkitTapHighlightColor: 'transparent'
      }}
    >
      {/* Notification subtile pour les favoris */}
      {showNotification && (
        <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-50 bg-white dark:bg-gray-800 px-4 py-2 rounded-full shadow-lg border-2 border-accent-400 dark:border-accent-500 animate-fade-in">
          <span className="text-sm font-semibold text-primary-900 dark:text-primary-300 flex items-center gap-2">
            <i className={`fas fa-heart ${isFavorite(produit._id) ? 'text-red-500' : 'text-gray-400'}`}></i>
            {notificationMessage}
          </span>
        </div>
      )}
      
      {/* Motif décoratif subtil au hover */}
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
        <div 
          className="absolute inset-0" 
          style={{ 
            backgroundImage: 'url(/images/patterns.jpeg)',
            backgroundSize: '150px 150px',
            opacity: 0.03
          }}
        />
      </div>
      
      {/* Image cliquable pour aller au détail */}
      <Link 
        to={`/produits/${produit._id}`} 
        className="block relative z-10 flex-shrink-0"
        onClick={(e) => {
          // Empêcher la navigation si on clique sur un bouton d'action
          const target = e.target;
          if (target.closest('button') || target.closest('[role="button"]') || target.closest('a[href*="wa.me"]')) {
            e.preventDefault();
            e.stopPropagation();
            return false;
          }
        }}
        style={{ 
          touchAction: 'manipulation',
          WebkitTapHighlightColor: 'transparent'
        }}
      >
        <div className="relative h-48 overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-600">
          <img
            src={getImageUrl(produit.photos?.[0])}
            alt={produit.nom}
            className="w-full h-full object-cover transition-transform duration-500 md:group-hover:scale-110"
            onError={(e) => {
              e.target.src = '/images/placeholder-product.svg';
            }}
          />
          {produit.pourcentageReduction > 0 && (
            <div className="absolute top-2 left-2 bg-gradient-to-r from-red-500 to-red-600 text-white px-2 py-1 rounded-full text-xs font-bold shadow-lg animate-pulse">
              -{produit.pourcentageReduction}%
            </div>
          )}
          {/* Tag pour les vues en bas à gauche */}
          {showViews && (
            <div className="absolute bottom-2 left-2 bg-black/60 dark:bg-black/80 backdrop-blur-sm text-white px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1">
              <i className="fas fa-eye text-xs"></i>
              <span>{produit.vues || 0}</span>
            </div>
          )}
        </div>
      </Link>

      {/* Contenu de la carte */}
      <div className="p-3 sm:p-4 flex flex-col flex-1 min-h-0">
        {/* Nom et Prix */}
        <div className="mb-3 space-y-2">
          <Link 
            to={`/produits/${produit._id}`}
            className="block"
            onClick={(e) => {
              const target = e.target;
              if (target.closest('button') || target.closest('[role="button"]') || target.closest('a[href*="wa.me"]')) {
                e.preventDefault();
                e.stopPropagation();
                return false;
              }
            }}
            style={{ 
              touchAction: 'manipulation',
              WebkitTapHighlightColor: 'transparent'
            }}
          >
            <h3 className="font-display text-sm sm:text-base font-semibold text-primary-900 dark:text-primary-300 line-clamp-2 hover:text-accent-600 dark:hover:text-accent-400 transition-colors leading-tight">
              {produit.nom}
            </h3>
          </Link>
          <div className="flex items-baseline gap-2">
            {produit.pourcentageReduction > 0 ? (
              <>
                <span className="text-gray-400 dark:text-gray-500 line-through text-xs sm:text-sm">{produit.prixOriginal}F</span>
                <span className="text-lg sm:text-xl font-bold text-red-600 dark:text-red-400">{produit.prix}F</span>
              </>
            ) : (
              <span className="text-lg sm:text-xl font-bold text-primary-800 dark:text-primary-400">{produit.prix}F</span>
            )}
            <span className="text-gray-500 dark:text-gray-400 text-xs">CFA</span>
          </div>
        </div>

        {/* Bouton Commander WhatsApp - Toujours visible */}
        {produit.artisan?.whatsapp ? (
          <a
            href={generateWhatsAppMessage()}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => {
              // Empêcher la propagation vers le Link parent
              e.stopPropagation();
            }}
            className="w-full bg-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-600 text-white px-3 py-2.5 rounded-lg font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-green-300 flex items-center justify-center gap-2 shadow-sm hover:shadow-md text-sm"
            aria-label="Commander via WhatsApp"
          >
            <i className="fab fa-whatsapp text-base" aria-hidden="true"></i>
            <span>Commander</span>
          </a>
        ) : (
          <Link 
            to={`/produits/${produit._id}`}
            className="w-full bg-primary-600 hover:bg-primary-700 dark:bg-primary-700 dark:hover:bg-primary-600 text-white px-3 py-2.5 rounded-lg font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-300 flex items-center justify-center gap-2 shadow-sm hover:shadow-md text-center text-sm"
          >
            <i className="fas fa-info-circle text-base" aria-hidden="true"></i>
            <span>Voir les détails</span>
          </Link>
        )}
      </div>

      <div className="absolute top-2 right-2 flex flex-col space-y-1 opacity-100 transition-all duration-300 z-20">
        <Button 
          variant="icon" 
          size="sm" 
          ariaLabel={isFavorite(produit._id) ? "Retirer des favoris" : "Ajouter aux favoris"} 
          onClick={handleAddFavorite} 
          className={isFavorite(produit._id) ? 'text-red-500 bg-white dark:bg-gray-700 w-8 h-8 shadow-lg' : 'hover:text-red-500 bg-white dark:bg-gray-700 w-8 h-8 shadow-lg'}
          style={{ touchAction: 'manipulation' }}
        >
          <i className={`text-sm ${isFavorite(produit._id) ? 'fas fa-heart' : 'far fa-heart'}`} aria-hidden="true"></i>
        </Button>
        <Button 
          variant="icon" 
          size="sm" 
          ariaLabel="Partager" 
          onClick={handleShare}
          className="hover:text-accent-600 dark:hover:text-accent-400 bg-white dark:bg-gray-700 w-8 h-8 shadow-lg"
          style={{ touchAction: 'manipulation' }}
        >
          <i className="fas fa-share-alt text-sm" aria-hidden="true"></i>
        </Button>
      </div>
    </div>
  );
};

export default ProduitCard;